import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { flushSync } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type {
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import type { Dayjs } from "dayjs";
import { pdfjs } from "react-pdf";
import AiImpactAssessmentFilterBar from "./AiImpactAssessmentFilterBar";
import AssessmentCenterImpactDataGrid from "./AssessmentCenterImpactDataGrid";
import StartManualAssessmentDialog, {
  type ManualAssessmentEntrySource,
} from "./StartManualAssessmentDialog";
import {
  createAiImpactAssessmentColumns,
  type AiImpactAssessmentRow,
} from "./assessmentCenterColumns";
import {
  buildConceptAssessmentResultFromCenterRow,
  getAssessmentCenterList,
  mapAssessmentCenterItemToGridRow,
  type AiImpactAssessmentGridRow,
  type AssessmentFilterOptionsResponse,
} from "../../services/assessmentCenterService";
import type { StartManualAssessmentRegulationContext } from "../../services/manualAssessmentService";
import type { TriageImpactedItem } from "../../services/manualAssessmentService";
import { resolveAssessmentFilterOptionQueryValues } from "../../utils/assessmentHistoryStatus";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { PDFViewer } from "../RegulationDetails/PDFViewer";
import {
  useAssessmentCenterPaginationHandlers,
  useClampAssessmentCenterPage,
  useStableServerRowCount,
} from "./useAssessmentCenterPagination";
import { useAiImpactAssessmentRowActions } from "./useAiImpactAssessmentRowActions";

const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: "executedDate", sort: "desc" },
];

// Configure PDF.js worker (same as Regulation Details).
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Maps grid `field` → `GET /assessment/histories` `sort_by`.
 * Default when unset: `executed_date` desc.
 *
 * Product sort rules: numeric (regulation id, version), date (executed),
 * alphabetical (`execute_by`, `by`), semantic (impact level),
 * non-sortable columns are disabled in column defs.
 */
const SORT_FIELD_TO_API: Record<string, string> = {
  regulationId: "regulation_id",
  version: "regulation_version",
  executedDate: "executed_date",
  executedBy: "execute_by",
  assessmentType: "by",
  impactLevel: "impacted_level",
};

function sortModelToApiParams(model: GridSortModel): {
  sort_by?: string;
  sort_order?: "asc" | "desc";
} {
  const first = model[0];
  if (!first?.sort) {
    return { sort_by: "executed_date", sort_order: "desc" };
  }
  const sort_by = SORT_FIELD_TO_API[first.field] ?? "executed_date";
  return { sort_by, sort_order: first.sort };
}

/**
 * AI Impact Assessment tab — filter strip + DataGrid Premium fed by GET /assessment/histories.
 */
interface AiImpactAssessmentTableProps {
  readonly onOpenPlanManagement?: () => void;
}

export default function AiImpactAssessmentTable({
  onOpenPlanManagement,
}: AiImpactAssessmentTableProps) {
  const queryClient = useQueryClient();
  const canRerunSystem = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  const canExecuteManualAssessment = useHasPrivilege(
    "gri.assessment.manual.execute",
  );
  const canReadChecklist = useHasPrivilege("gri.assessment.checklist");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const { handleSortModelChange, handlePaginationModelChange } =
    useAssessmentCenterPaginationHandlers(setPaginationModel, setSortModel);

  const [filters, setFilters] = useState<{
    status: string;
    executedBy: string;
    assessmentType: string;
    impactLevel: string;
    executedDate: Dayjs | null;
  }>({
    status: "",
    executedBy: "",
    assessmentType: "",
    impactLevel: "",
    executedDate: null,
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const {
    actionHandlers,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog,
    isRetryBusy,
  } = useAiImpactAssessmentRowActions({ canRerunSystem });
  const [startManualDialogOpen, setStartManualDialogOpen] = useState(false);
  const [startManualImpactedItems, setStartManualImpactedItems] = useState<
    readonly TriageImpactedItem[] | undefined
  >(undefined);
  const [startManualRegulation, setStartManualRegulation] = useState<
    StartManualAssessmentRegulationContext | undefined
  >(undefined);
  const [startManualConceptResult, setStartManualConceptResult] = useState<
    ReturnType<typeof buildConceptAssessmentResultFromCenterRow> | null
  >(null);
  const [startManualEntrySource, setStartManualEntrySource] =
    useState<ManualAssessmentEntrySource>("direct");

  const listFilterOptionsRef = useRef<AssessmentFilterOptionsResponse>([]);

  const handleStartManualAssessment = (row: AiImpactAssessmentRow) => {
    const gridRow = row as AiImpactAssessmentGridRow;
    setStartManualRegulation({
      regulationId: gridRow.regulationId,
      regulationVersion: gridRow.version,
      regulationTitle: gridRow.regulation || gridRow.regulationId,
    });
    const items =
      gridRow.impactedItems && gridRow.impactedItems.length > 0
        ? gridRow.impactedItems
        : undefined;
    setStartManualImpactedItems(items);
    setStartManualConceptResult(
      items ? buildConceptAssessmentResultFromCenterRow(gridRow) : null,
    );
    setStartManualEntrySource("from_ai_impact");
    setStartManualDialogOpen(true);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [
    debouncedFilters.status,
    debouncedFilters.executedBy,
    debouncedFilters.assessmentType,
    debouncedFilters.impactLevel,
    debouncedFilters.executedDate,
  ]);

  const sortParams = useMemo(() => {
    // Always send default sort to backend unless user overrides it.
    return sortModelToApiParams(sortModel);
  }, [sortModel]);

  /** Flush debounced filters so refresh uses the same params as the visible filter bar. */
  const handleRefreshAssessmentList = useCallback(() => {
    flushSync(() => {
      setDebouncedFilters(filters);
    });
    void queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
  }, [filters, queryClient]);

  const { data, isLoading, isFetching } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "assessment-center",
      debouncedFilters.status,
      debouncedFilters.executedBy,
      debouncedFilters.assessmentType,
      debouncedFilters.impactLevel,
      debouncedFilters.executedDate?.toISOString() ?? "",
      paginationModel.page,
      paginationModel.pageSize,
      sortParams.sort_by ?? "",
      sortParams.sort_order ?? "",
    ],
    queryFn: () => {
      const fromList = listFilterOptionsRef.current;
      const fromCache =
        queryClient.getQueryData<AssessmentFilterOptionsResponse>([
          "assessment-center",
          "ai",
          "filter-options",
        ]) ?? [];
      let filterOpts: AssessmentFilterOptionsResponse = [];
      if (fromList.length > 0) {
        filterOpts = fromList;
      } else if (fromCache.length > 0) {
        filterOpts = fromCache;
      }

      const statusParam = resolveAssessmentFilterOptionQueryValues(
        filterOpts.length > 0 ? filterOpts : undefined,
        "status",
        debouncedFilters.status,
      );

      return getAssessmentCenterList({
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        sort_by: sortParams.sort_by || undefined,
        sort_order: sortParams.sort_order || undefined,
        status: statusParam,
        executed_by: debouncedFilters.executedBy || undefined,
        by: debouncedFilters.assessmentType
          ? [debouncedFilters.assessmentType]
          : undefined,
        impacted_level: debouncedFilters.impactLevel
          ? [debouncedFilters.impactLevel.toLowerCase()]
          : undefined,
        date: debouncedFilters.executedDate
          ? debouncedFilters.executedDate.format("YYYY-MM-DD")
          : undefined,
      });
    },
  });

  useEffect(() => {
    if (data?.filterOptions?.length) {
      listFilterOptionsRef.current = data.filterOptions;
    }
  }, [data?.filterOptions]);

  useClampAssessmentCenterPage(data?.total, setPaginationModel);
  const stableRowCount = useStableServerRowCount(data?.total);

  const rows = useMemo(
    () =>
      (data?.items ?? []).map((item) => mapAssessmentCenterItemToGridRow(item)),
    [data?.items],
  );

  const columns = useMemo(
    () =>
      createAiImpactAssessmentColumns({
        ...actionHandlers,
        onStartManualAssessment: canExecuteManualAssessment
          ? handleStartManualAssessment
          : undefined,
      }),
    [actionHandlers, canExecuteManualAssessment, handleStartManualAssessment],
  );

  const seedRegulationId = rows[0]?.regulationId ?? null;
  const seedRegulationVersion = rows[0]?.version ?? null;

  const showStrongLoading = isFetching || isRetryBusy;

  return (
    <>
      <Backdrop
        open={showStrongLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          position: "absolute",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={24} />
          <Typography sx={{ fontWeight: 600 }}>
            {isRetryBusy ? "Starting re-run…" : "Loading…"}
          </Typography>
        </Box>
      </Backdrop>
      <Box
        sx={{
          backgroundColor: "#fff",
          pt: 2,
          pb: 2,
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AiImpactAssessmentFilterBar
          onOpenPlanManagement={
            canReadChecklist ? onOpenPlanManagement : undefined
          }
          onRefresh={handleRefreshAssessmentList}
          seedRegulationId={seedRegulationId}
          seedRegulationVersion={seedRegulationVersion}
          filterConfigsOverride={data?.filterOptions}
          executedDate={filters.executedDate}
          onExecutedDateChange={(next) =>
            setFilters((prev) => ({ ...prev, executedDate: next }))
          }
          status={filters.status}
          onStatusChange={(next) => setFilters((p) => ({ ...p, status: next }))}
          executedBy={filters.executedBy}
          onExecutedByChange={(next) =>
            setFilters((p) => ({ ...p, executedBy: next }))
          }
          assessmentType={filters.assessmentType}
          onAssessmentTypeChange={(next) =>
            setFilters((p) => ({ ...p, assessmentType: next }))
          }
          impactLevel={filters.impactLevel}
          onImpactLevelChange={(next) =>
            setFilters((p) => ({ ...p, impactLevel: next }))
          }
        />
        <Box
          sx={{
            // mt: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AssessmentCenterImpactDataGrid
            embedded
            fillHeight
            ariaLabel="AI impact assessment list"
            rows={rows}
            loading={isLoading || isFetching}
            rowCount={stableRowCount}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
          />
        </Box>
      </Box>

      <PDFViewer
        open={pdfDialogOpen}
        pdfUrl={selectedPdfUrl}
        title="PDF Preview"
        onClose={closePdfDialog}
      />

      <StartManualAssessmentDialog
        open={startManualDialogOpen}
        regulation={
          startManualRegulation ?? {
            regulationId: "",
            regulationVersion: "",
            regulationTitle: "",
          }
        }
        onClose={() => {
          setStartManualDialogOpen(false);
          setStartManualImpactedItems(undefined);
          setStartManualRegulation(undefined);
          setStartManualConceptResult(null);
          setStartManualEntrySource("direct");
        }}
        entrySource={startManualEntrySource}
        impactedItems={startManualImpactedItems}
        conceptAssessmentResult={startManualConceptResult}
        onInitiated={() => {
          void queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
        }}
      />
    </>
  );
}
